import { subDays, subHours } from 'date-fns';
import { Checkbox } from 'primereact/checkbox';
import { Divider } from 'primereact/divider';
import React, { useCallback, useMemo, useState } from 'react';
import {
    BodySmallEmphasized,
    BodySmallRegular,
    H6Emphasized,
    Spinner,
    XStack,
    YStack,
} from 'smoothie';

import { Button } from '../../common/components/GroveButtons';
import {
    useGetDbosDurationStats,
    useGetDbosQueues,
    useGetDbosQueueTimeline,
    useGetDbosScheduledCrons,
    useGetDbosSummary,
    useGetDbosTimeline,
} from '../hooks/useDbosData';
import type { TimelineInterval } from '../types/DbosTypes';
import { DbosCronStatus } from './DbosCronStatus';
import { DbosDurationStats } from './DbosDurationStats';
import { DbosQueueStatus } from './DbosQueueStatus';
import { DbosWorkflowDetailModal } from './DbosWorkflowDetailModal';
import { DbosWorkflowExplorer } from './DbosWorkflowExplorer';
import { DbosWorkflowSummary } from './DbosWorkflowSummary';
import { DbosWorkflowTimeline } from './DbosWorkflowTimeline';

type TimeRangePreset = '1h' | '6h' | '24h' | '7d';

const PRESET_LABELS: Record<TimeRangePreset, string> = {
    '1h': 'Last 1h',
    '6h': 'Last 6h',
    '24h': 'Last 24h',
    '7d': 'Last 7d',
};

const getTimeRange = (
    preset: TimeRangePreset,
): { startTime: string; endTime: string } => {
    const now = new Date();
    const endTime = now.toISOString();
    let startTime: string;

    switch (preset) {
        case '1h':
            startTime = subHours(now, 1).toISOString();
            break;
        case '6h':
            startTime = subHours(now, 6).toISOString();
            break;
        case '24h':
            startTime = subDays(now, 1).toISOString();
            break;
        case '7d':
            startTime = subDays(now, 7).toISOString();
            break;
    }

    return { startTime, endTime };
};

const DEFAULT_INTERVAL_FOR_PRESET: Record<TimeRangePreset, TimelineInterval> = {
    '1h': '5min',
    '6h': '15min',
    '24h': 'hour',
    '7d': 'day',
};

const AUTO_REFRESH_INTERVAL_MS = 30_000;

// Stable empty params reference -- avoids creating a new {} on every render
// which would cause React Query to think the params changed.
const EMPTY_PARAMS: Record<string, never> = {};

// Shared query options to keep requests under control:
// - staleTime: data stays fresh for 60s, no background re-fetch during that window
// - retry: only 1 retry on failure instead of default 3
// - refetchOnWindowFocus: don't re-fire all queries when tab regains focus
const SHARED_QUERY_OPTIONS = {
    staleTime: 60_000,
    retry: 1,
    refetchOnWindowFocus: false as const,
};

export const DbosDashboard = () => {
    // --- Global state ---
    const [timeRangePreset, setTimeRangePreset] =
        useState<TimeRangePreset>('24h');
    const [autoRefresh, setAutoRefresh] = useState(false);
    const [timelineInterval, setTimelineInterval] = useState<TimelineInterval>(
        DEFAULT_INTERVAL_FOR_PRESET['24h'],
    );
    const [timelineWorkflowFilter, setTimelineWorkflowFilter] = useState<
        string | undefined
    >(undefined);
    const [selectedWorkflowId, setSelectedWorkflowId] = useState<string | null>(
        null,
    );
    const [isDetailOpen, setIsDetailOpen] = useState(false);

    // Memoize time range so it only recomputes when the preset changes,
    // NOT on every render. Without this, new Date() produces a different
    // ISO string on each render, causing React Query to see new query keys
    // and fire requests in an infinite loop.
    const { startTime, endTime } = useMemo(
        () => getTimeRange(timeRangePreset),
        [timeRangePreset],
    );

    const handlePresetChange = useCallback((preset: TimeRangePreset) => {
        setTimeRangePreset(preset);
        setTimelineInterval(DEFAULT_INTERVAL_FOR_PRESET[preset]);
    }, []);

    // --- Data hooks ---
    const refetchInterval = autoRefresh ? AUTO_REFRESH_INTERVAL_MS : undefined;

    const summaryQuery = useGetDbosSummary(
        { startTime, endTime },
        { ...SHARED_QUERY_OPTIONS, refetchInterval },
    );

    const timelineQuery = useGetDbosTimeline(
        {
            startTime,
            endTime,
            interval: timelineInterval,
            workflowName: timelineWorkflowFilter,
        },
        { ...SHARED_QUERY_OPTIONS, refetchInterval },
    );

    const queuesQuery = useGetDbosQueues(EMPTY_PARAMS, {
        ...SHARED_QUERY_OPTIONS,
        refetchInterval,
    });

    const queueTimelineQuery = useGetDbosQueueTimeline(
        { startTime, endTime },
        { ...SHARED_QUERY_OPTIONS, refetchInterval },
    );

    const durationStatsQuery = useGetDbosDurationStats(
        { startTime, endTime },
        { ...SHARED_QUERY_OPTIONS, refetchInterval },
    );

    const cronsQuery = useGetDbosScheduledCrons(EMPTY_PARAMS, {
        ...SHARED_QUERY_OPTIONS,
        refetchInterval,
    });

    // --- Handlers ---
    const handleSelectWorkflow = useCallback((workflowId: string) => {
        setSelectedWorkflowId(workflowId);
        setIsDetailOpen(true);
    }, []);

    const handleCloseDetail = useCallback(() => {
        setIsDetailOpen(false);
        setSelectedWorkflowId(null);
    }, []);

    const handleNavigateToWorkflow = useCallback((workflowId: string) => {
        setSelectedWorkflowId(workflowId);
        // Modal stays open, will re-fetch with new workflowId
    }, []);

    // --- Derived data ---
    const availableWorkflowNames = useMemo(
        () => summaryQuery.data?.byWorkflow.map((w) => w.workflowName) ?? [],
        [summaryQuery.data],
    );

    const isAnythingLoading =
        summaryQuery.isLoading &&
        timelineQuery.isLoading &&
        queuesQuery.isLoading;

    if (isAnythingLoading) {
        return (
            <YStack alignItems="center" padding={40}>
                <Spinner />
                <BodySmallRegular color="$gray1000">
                    Loading DBOS Dashboard...
                </BodySmallRegular>
            </YStack>
        );
    }

    return (
        <YStack gap={24} width="100%">
            {/* === Global Controls === */}
            <XStack
                justifyContent="space-between"
                alignItems="center"
                width="100%"
                flexWrap="wrap"
                gap={8}
            >
                <XStack gap={8} alignItems="center">
                    <H6Emphasized>DBOS Workflow Dashboard</H6Emphasized>
                </XStack>
                <XStack gap={8} alignItems="center">
                    {/* Time Range Presets */}
                    {(Object.keys(PRESET_LABELS) as TimeRangePreset[]).map(
                        (preset) => (
                            <Button
                                key={preset}
                                variant={
                                    timeRangePreset === preset
                                        ? 'primary'
                                        : 'tertiary'
                                }
                                size="small"
                                onPress={() => {
                                    handlePresetChange(preset);
                                }}
                            >
                                {PRESET_LABELS[preset]}
                            </Button>
                        ),
                    )}

                    {/* Auto-refresh toggle */}
                    <XStack gap={4} alignItems="center" marginLeft={8}>
                        <Checkbox
                            checked={autoRefresh}
                            onChange={(e) => {
                                setAutoRefresh(e.checked ?? false);
                            }}
                        />
                        <BodySmallRegular color="$gray1000">
                            Auto-refresh (30s)
                        </BodySmallRegular>
                    </XStack>
                </XStack>
            </XStack>

            {/* === Section 1: Summary Cards === */}
            {summaryQuery.data != null && (
                <DbosWorkflowSummary data={summaryQuery.data} />
            )}
            {summaryQuery.error != null && (
                <BodySmallEmphasized color="$red1100">
                    Failed to load summary:{' '}
                    {summaryQuery.error instanceof Error
                        ? summaryQuery.error.message
                        : 'Unknown error'}
                </BodySmallEmphasized>
            )}

            <Divider />

            {/* === Section 2: Workflow Timeline === */}
            <DbosWorkflowTimeline
                buckets={timelineQuery.data?.buckets ?? []}
                interval={timelineInterval}
                onIntervalChange={setTimelineInterval}
                workflowNameFilter={timelineWorkflowFilter}
                onWorkflowNameFilterChange={setTimelineWorkflowFilter}
                availableWorkflowNames={availableWorkflowNames}
            />

            <Divider />

            {/* === Section 3: Queues + Duration Stats (Two Columns) === */}
            <XStack gap={24} width="100%" flexWrap="wrap">
                <YStack flex={1} minWidth={400}>
                    <DbosQueueStatus
                        queues={queuesQuery.data?.queues ?? []}
                        timelineBuckets={queueTimelineQuery.data?.buckets ?? []}
                    />
                </YStack>
                <YStack flex={1} minWidth={400}>
                    <DbosDurationStats
                        stats={durationStatsQuery.data?.stats ?? []}
                    />
                </YStack>
            </XStack>

            <Divider />

            {/* === Section 4: Cron Jobs === */}
            <DbosCronStatus crons={cronsQuery.data?.crons ?? []} />

            <Divider />

            {/* === Section 5: Workflow Explorer === */}
            <DbosWorkflowExplorer
                startTime={startTime}
                endTime={endTime}
                onSelectWorkflow={handleSelectWorkflow}
            />

            {/* === Workflow Detail Modal === */}
            <DbosWorkflowDetailModal
                workflowId={selectedWorkflowId}
                isOpen={isDetailOpen}
                onClose={handleCloseDetail}
                onNavigateToWorkflow={handleNavigateToWorkflow}
            />
        </YStack>
    );
};
